/**
 * MapSearchSection manages the hotel map, area selection, and search form logic for the top page.
 * Handles complex logic for map interactions, form state, and hotel/area data processing.
 */
"use client";

import * as React from "react";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import dynamic from "next/dynamic";
import { getPublicEnv } from '@/utils/env';
import type {
  JapanArea,
  SelectedLocation,
  selectedLocationType as selectedLocationTypeImport,
} from "../../map-view/japan-map/japanCountryMap";

// Lazy load the JapanHotelMap component (saves ~100KB from initial bundle)
const JapanHotelMap = dynamic(
  () => import("../../map-view/japan-map/japanCountryMap"),
  {
    ssr: false,
    loading: () => (
      <div className="flex h-[600px] w-full items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="mb-2 text-lg font-semibold">地図を読み込んでいます...</div>
          <div className="h-2 w-48 overflow-hidden rounded-full bg-gray-200">
            <div className="h-full animate-pulse bg-blue-500"></div>
          </div>
        </div>
      </div>
    ),
  }
);

// Import the selectedLocationType as a regular import for use in the component
const selectedLocationType: typeof selectedLocationTypeImport = {
  REGION: "region",
  PREFECTURE: "prefecture",
  TOKYO_23_WARDS: "tokyo-23-wards",
  OSAKA_24_WARDS: "osaka-24-wards",
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as any;
import {
  getAreaHotelThumbnail,
  getSpecialAreaName,
  MapBrandIcon,
} from "@app/[locale]/tourist-attraction/[city_slug]/[slug]/util";
import {
  DEFAULT_CHECKIN_DATE,
  DEFAULT_CHECKOUT_DATE,
  DEFAULT_ADULTS,
} from "@/constants";
import { TRIPLA_BOOKING_URL } from "@/constants/enviroment";
import useJapanAreasStore from "@/store/hooks/useGlobal";
import { HotelListParams, ERoomType, ERoomOrderBy } from "@/types/roomsList";
import { LOCALE_BASE_MAP } from "@/i18n/routing";
import {
  groupJapanAreas,
  chunkArray,
  TYPE_GROUP,
  redirectBookingCard,
  formatDateToYYYYMMDD,
  formatDateForStorage,
} from "@/utils";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { TypeSearch } from "../../hotel-result";
import { findOption } from "../../search-avaliable/findOption";
import { schemaSearchAvailableRoom } from "../../search-avaliable/SearchAvailableRoomMobile";
import { IMapFilterForm } from "./MapFilter";
import { SelectOption } from "../../search-avaliable/SearchAvailableRoom";


import { scrollToElementWithOffset } from "@/utils/scrollOffset/scrollOffset";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { HotelBrandItemNullable } from "@app/[locale]/brands/[brand_slug]/page";
import MapFilterCheckboxes from "./MapFilterCheckboxes";
import {
  HotelBenefitListQuery,
  JapanHotel,
  RegionListQuery,
  GetMapTextDatasourceQuery,
  HotelBenefit,
  UploadFile,
  GetMapTextDatasourceQueryVariables,
  JapanArea as JapanAreaType,
} from "@/types/generated";
import {
  handleBenefitListData,
  handleBrandListData,
  getSearchRoomData,
} from "../../hotel-result/filter/utils";
import HotelListSlider, {
  IHotelListSliderProps,
  THotelData,
} from "../../hotel-list-slider";

import { getLowestPriceHotels } from "@app/api/tripla/hotel";
// import tokyoWardsHotels from "../../map-view/japan-map/toykyoWardMap.json";
// import osakaWardsHotels from "../../map-view/japan-map/osakaWardMap.json";
import { iconList } from "@/assets/icons";
import api from "@/lib/api";
import { createBookingParams } from "../../search-avaliable/ultils";
import { Divider } from "@/components/atoms/divider";
import { useQuery } from "@tanstack/react-query";
import { fetchCMS } from "@/graphql/client/client";

import { GET_MAP_TEXT_DATASOURCE } from "@/queries/topPageMapSearchQueries";
import { DateRange } from "react-day-picker";
import { IBanner } from "../../hotel-result/banner/BannerBrand";
import useSearchFormStore from "@/store/searchFormStore/searchFormStore";
import { useHasHydrated } from "@/hooks/useHasHydrated/useHasHydrated";
import { Modal } from "@/components/atoms/modal";
import { Button } from "@/components/atoms/button";
import { transformJapanHotels, transformOsakaHotels, TokyoWardMapHotel, OsakaWardHotelSimple, transformHotelsForMap } from "@/utils/mapUtils";
// Using `unknown` here is intentional as the codebase is tightly coupled with existing generic types.
// Changing or removing it may affect type inference and potentially break the build.
interface ServiceWithDocumentId {
  documentId: string;
  [key: string]: unknown;
}
interface CityWithOrder {
  documentId?: string;
  order?: number;
  [key: string]: unknown;
}

// Interface for mixed hotel data from different sources
interface MixedHotelData {
  item_name?: string;
  display_name?: string;
  name?: string;
  properties?: {
    hotel_name?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

// Interface for region datasource items
interface RegionDatasourceItem {
  documentId?: string;
  order?: number;
  cities_order_on_top_page?: CityWithOrder[];
  [key: string]: unknown;
}

interface SelectedRegionItem {
  documentId?: string;
  order?: number;
  [key: string]: unknown;
}

// Minimal hotel data structure for map display (reduces HTML payload significantly)
export interface MinimalHotelForMap {
  slug?: string | null;
  name?: string | null;
  properties?: {
    latitude?: string | null;
    longitude?: string | null;
    adjust_latitude?: number | null;
    adjust_longtitude?: number | null;
    brand?: unknown;
    prefecture?: string | null;
  } | null;
  amenities_and_services?: {
    hotel_benefits?: unknown;
  } | null;
}

// Lean hotel list structure - only contains minimal fields needed for map display
export interface LeanHotelList {
  japanHotels: MinimalHotelForMap[];
}

export interface IMapSearchSectionProps {
  dataFilter?: IMapFilterForm | null;
  benefitOption?: { value: string; label: string }[];
  brandsOption?: { value: string; label: string }[];
  defaultValue?: IMapFilterForm | null;
  currentBrand?: HotelBrandItemNullable;
  areasData: JapanArea[];
  locale: string;
  benefitList?: HotelBenefitListQuery;
  brandList?: HotelBrandItemNullable[];
  currentCity?: string;
  brands: MapBrandIcon[];
  listHotel?: string[];
  brandBannerList: IBanner[];
  hotelSliderSections: IHotelListSliderProps[];
  regionData?: RegionListQuery;
  /** Lean hotel data for map display - uses MAP_SEARCH_HOTEL_LIST query */
  hotel: LeanHotelList;
  enrichedRegionData?: unknown;
}

const IsolatedMapComponent = React.memo(
  ({
    areasData,
    locale,
    brands,
    selectedRegion,
    japanAreas,
    onLocationSelect,
    onHotelClick,
    mapFilterCriteria,
    hotelMetaData,
    tokyoHotelsAlt,
    enrichedRegionData
  }: {
    areasData: JapanArea[];
    locale: string;
    brands: MapBrandIcon[];
    selectedRegion: SelectedLocation | null;
    japanAreas?: JapanArea;
    onLocationSelect?: (location: SelectedLocation, fromMap?: boolean) => void;
    onHotelClick?: (hotelDocumentId: string) => void;
    mapFilterCriteria?: { brands?: string[]; benefits?: string[] } | null;
    hotelMetaData?: ReturnType<typeof transformHotelsForMap>;
    tokyoHotelsAlt?: TokyoWardMapHotel[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    enrichedRegionData: any
  }) => {
    const handleLocationSelect = useCallback(
      (location: SelectedLocation) => {
        if (onLocationSelect) {
          onLocationSelect(location, true);
        } else {
          globalThis.dispatchEvent(
            new CustomEvent("mapLocationSelect", { detail: location }),
          );
        }
      },
      [onLocationSelect],
    );

    const handleHotelClick = useCallback(
      (hotelDocumentId: string) => {
        if (onHotelClick) {
          onHotelClick(hotelDocumentId);
        } else {
          globalThis.dispatchEvent(
            new CustomEvent("mapHotelClick", { detail: hotelDocumentId }),
          );
        }
      },
      [onHotelClick],
    );
    return (
      <JapanHotelMap
        locale={locale}
        areasData={areasData}
        brands={brands}
        onLocationSelect={handleLocationSelect}
        onHotelClick={handleHotelClick}
        selectedRegion={selectedRegion}
        japanAreasWithBenefits={japanAreas as unknown as JapanAreaType[]}
        mapFilterCriteria={mapFilterCriteria}
        hotelMetaData={hotelMetaData || []}
        tokyoHotelsAlt={tokyoHotelsAlt}
        enrichedRegionData={enrichedRegionData}
      />
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.locale !== nextProps.locale) return false;
    if (prevProps.areasData !== nextProps.areasData) return false;
    if (prevProps.brands !== nextProps.brands) return false;

    if (prevProps.selectedRegion === nextProps.selectedRegion) return true;
    if (!prevProps.selectedRegion || !nextProps.selectedRegion) return false;

    return (
      prevProps.selectedRegion.name === nextProps.selectedRegion.name &&
      prevProps.selectedRegion.type === nextProps.selectedRegion.type &&
      prevProps.selectedRegion.documentId ===
      nextProps.selectedRegion.documentId
    );
  },
);

IsolatedMapComponent.displayName = "IsolatedMapComponent";

class HotelListEventEmitter {
  private readonly listeners: ((sections: IHotelListSliderProps[]) => void)[] =
    [];

  public subscribe(callback: (sections: IHotelListSliderProps[]) => void) {
    this.listeners.push(callback);
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  public emit(sections: IHotelListSliderProps[]) {
    this.listeners.forEach((callback) => callback(sections));
  }
}

const hotelListEmitter = new HotelListEventEmitter();
// Not able to Move fetchCMS in parent component due to usage of useQuery hook here (caching the data).
export const useGetMapTextDatasourceQuery = () => {
  return useQuery<GetMapTextDatasourceQuery>({
    queryKey: ["get-map-text-Datasource"],
    queryFn: () =>
      fetchCMS<GetMapTextDatasourceQuery>(GET_MAP_TEXT_DATASOURCE, {
        homeDatasourceDocumentId: "d574dcbe9993fd0f95fede1b",
      } as GetMapTextDatasourceQueryVariables),
  });
};

/**
 * Converts a JapanHotel object from japanAreas to THotelData for hotel slider display.
 * Handles extraction of brand info, thumbnail, and hotel details.
 */
const convertJapanAreasHotelToTHotelData = (
  fullHotelFromJapanAreas: JapanHotel,
  area: JapanArea,
  brandList?: HotelBrandItemNullable[],
): THotelData => {
  const brandInfo = brandList?.find(
    (brand) =>
      brand?.documentId ===
      fullHotelFromJapanAreas?.properties?.brand?.[0]?.documentId,
  );
  const thumbnail = getAreaHotelThumbnail(fullHotelFromJapanAreas);
  let city = area?.name || "";

  if (area?.name === "Tokyo") {
    city = "東京";
  } else if (area?.name === "Osaka") {
    city = "大阪";
  }

  let minPrice: number | null = null;

  if (fullHotelFromJapanAreas.properties?.base_price) {
    minPrice = Number.parseInt(fullHotelFromJapanAreas.properties.base_price);
  } else if (fullHotelFromJapanAreas.properties?.highest_price_within_30_days) {
    minPrice = Number.parseInt(
      fullHotelFromJapanAreas.properties.highest_price_within_30_days,
    );
  }
  return {
    documentId: fullHotelFromJapanAreas.documentId,
    name:
      fullHotelFromJapanAreas.display_name ||
      fullHotelFromJapanAreas.hotel_name_in_mobile ||
      fullHotelFromJapanAreas.item_name ||
      fullHotelFromJapanAreas.properties?.hotel_name ||
      "Unknown Hotel",
    reviewCount: fullHotelFromJapanAreas.properties?.reviews || "0",
    rating: fullHotelFromJapanAreas.properties?.review_score || "0",
    city,
    description:
      fullHotelFromJapanAreas.properties?.hotel_overview_description ||
      fullHotelFromJapanAreas.properties?.custom_description ||
      fullHotelFromJapanAreas.properties?.guest_information ||
      "",
    image: thumbnail || {},
    brand: fullHotelFromJapanAreas.properties?.brand?.[0]?.documentId || "",
    service:
      fullHotelFromJapanAreas.amenities_and_services
        ?.amenities_and_services_customs || [],
    btnText: "詳細を見る",
    benefits:
      fullHotelFromJapanAreas.amenities_and_services?.hotel_benefits?.map(
        (b: HotelBenefit) => b.documentId,
      ) || [],
    lat: fullHotelFromJapanAreas.properties?.latitude,
    lng: fullHotelFromJapanAreas.properties?.longitude,
    slug: fullHotelFromJapanAreas.slug,
    withHotSprings:
      fullHotelFromJapanAreas.amenities_and_services?.amenities_and_services_customs?.some(
        (service: ServiceWithDocumentId) =>
          service.documentId === "HOT_SPRING_AMENITY",
      ) || false,
    externalUrl: fullHotelFromJapanAreas.redirect?.external_url,
    triplaCode:
      fullHotelFromJapanAreas.external_ids?.tripla_backend_code ||
      fullHotelFromJapanAreas.external_ids?.booking_id,
    brandItemName:
      fullHotelFromJapanAreas.properties?.brand?.[0]?.item_name ||
      fullHotelFromJapanAreas.properties?.brand?.[0]?.name ||
      brandInfo?.item_name,
    weather: fullHotelFromJapanAreas.weather || undefined,
    brandDocId: fullHotelFromJapanAreas.properties?.brand?.[0]?.documentId,
    triplaBookingCode:
      fullHotelFromJapanAreas.external_ids?.tripla_backend_code ||
      fullHotelFromJapanAreas.external_ids?.booking_id,
    minPrice,
    triplaId:
      fullHotelFromJapanAreas.external_ids?.tripla_backend_code ||
      fullHotelFromJapanAreas.external_ids?.booking_id ||
      "",
    brandImage:
      brandInfo?.logo_header?.cloudflare_url ||
      brandInfo?.logo_header_on_scroll?.cloudflare_url ||
      null,
  };
};

/**
 * Creates THotelData from area hotel data, used when full hotel data is not available in japanAreas.
 */
const createHotelDataFromAreaData = (
  hotel: JapanHotel,
  area: JapanArea,
  brandList?: HotelBrandItemNullable[],
): THotelData => {
  const brandInfo = brandList?.find(
    (brand) => brand?.documentId === hotel?.properties?.brand?.[0]?.documentId,
  );

  let cityName = area.name;
  if (area.name === "Tokyo") {
    cityName = "東京";
  }

  const hotelName =
    hotel.properties?.hotel_name ||
    hotel.hotel_name_in_mobile ||
    hotel.display_name ||
    hotel.item_name ||
    "Unknown Hotel";

  const imageUrl =
    hotel.hotel_images?.desktop?.[0]?.media ||
    hotel.hotel_images?.mobile?.[0]?.media ||
    {};

  const description =
    hotel?.properties?.hotel_overview_description ||
    hotel?.properties?.custom_description ||
    hotel?.properties?.guest_information ||
    "";

  const services =
    hotel.amenities_and_services?.amenities_and_services_customs || [];

  const benefits =
    hotel.amenities_and_services?.hotel_benefits?.map(
      (b: HotelBenefit) => b.documentId,
    ) || [];
  let minPrice: number | null = null;

  if (hotel?.properties?.base_price) {
    minPrice = Number.parseInt(hotel.properties.base_price);
  } else if (hotel?.properties?.highest_price_within_30_days) {
    minPrice = Number.parseInt(hotel.properties.highest_price_within_30_days);
  }

  return {
    documentId: hotel?.documentId,
    name: hotelName,
    reviewCount: hotel?.properties?.reviews || "0",
    rating: hotel?.properties?.review_score || "0",
    city: cityName,
    description: description,
    image: imageUrl as UploadFile,
    brand: hotel?.properties?.brand?.[0]?.documentId || "",
    service: services,
    btnText: "詳細を見る",
    benefits: benefits,
    lat: hotel?.properties?.latitude,
    lng: hotel?.properties?.longitude,
    slug: hotel?.slug,
    withHotSprings:
      services.some(
        (service: ServiceWithDocumentId) =>
          service.documentId === "HOT_SPRING_AMENITY",
      ) || false,
    externalUrl: hotel.redirect?.external_url,
    triplaCode:
      hotel.external_ids?.tripla_backend_code || hotel.external_ids?.booking_id,
    brandItemName:
      hotel?.properties?.brand?.[0]?.item_name ||
      hotel?.properties?.brand?.[0]?.name ||
      brandInfo?.item_name,
    weather: hotel.weather || undefined,
    brandDocId: hotel?.properties?.brand?.[0]?.documentId,
    triplaBookingCode:
      hotel.external_ids?.tripla_backend_code || hotel.external_ids?.booking_id,
    minPrice,
    triplaId:
      hotel.external_ids?.tripla_backend_code ||
      hotel.external_ids?.booking_id ||
      "",
    brandImage:
      brandInfo?.logo_header?.cloudflare_url ||
      brandInfo?.logo_header_on_scroll?.cloudflare_url ||
      null,
  };
};

/**
 * Filters areas by region name using a region-to-prefecture mapping.
 */
const filterAreasByRegion = (
  areasData: JapanArea[],
  regionName: string,
): JapanArea[] => {
  const regionToPrefectures: Record<string, string[]> = {
    Hokkaido: ["Hokkaido"],
    Tohoku: ["Aomori", "Iwate", "Miyagi", "Akita", "Yamagata", "Fukushima"],
    Kanto: [
      "Ibaraki",
      "Tochigi",
      "Gunma",
      "Saitama",
      "Chiba",
      "Tokyo",
      "Kanagawa",
    ],
    Chubu: [
      "Niigata",
      "Toyama",
      "Ishikawa",
      "Fukui",
      "Yamanashi",
      "Nagano",
      "Gifu",
      "Shizuoka",
      "Aichi",
    ],
    Kansai: ["Mie", "Shiga", "Kyoto", "Osaka", "Hyogo", "Nara", "Wakayama"],
    Chugoku: ["Tottori", "Shimane", "Okayama", "Hiroshima", "Yamaguchi"],
    Shikoku: ["Tokushima", "Kagawa", "Ehime", "Kochi"],
    Kyushu: [
      "Fukuoka",
      "Saga",
      "Nagasaki",
      "Kumamoto",
      "Oita",
      "Miyazaki",
      "Kagoshima",
      "Okinawa",
    ],
  };

  const prefecturesInRegion = regionToPrefectures[regionName] || [regionName];
  return areasData.filter((area) => {
    const areaName = area.name || "";
    return prefecturesInRegion.includes(areaName);
  });
};

/**
 * Filters Tokyo area hotels to only include those matching 23 wards.
 */
const filterAreasByTokyo23Wards = (areasData: JapanArea[], tokyoHotels: TokyoWardMapHotel[]): JapanArea[] => {
  const tokyoArea = areasData.find((area) => area.name === "Tokyo");

  if (tokyoArea) {
    const tokyo23WardsNames = tokyoHotels.map(
      (hotel) => hotel.name,
    );

    const filteredTokyo = {
      ...tokyoArea,
      hotels: tokyoArea.hotels?.filter((hotel) => {
        const hotelData = hotel as unknown as MixedHotelData;
        const englishName = hotelData?.item_name;
        const displayName = hotelData?.display_name;
        const hotelName = hotelData?.properties?.hotel_name;
        const name = hotelData?.name;

        const matchingWardName = tokyo23WardsNames.find(
          (wardName) =>
            wardName === englishName ||
            wardName === displayName ||
            wardName === hotelName ||
            wardName === name ||
            (englishName && wardName.includes(englishName)) ||
            englishName?.includes(wardName) ||
            (displayName && wardName.includes(displayName)) ||
            displayName?.includes(wardName) ||
            (hotelName && wardName.includes(hotelName)) ||
            hotelName?.includes(wardName),
        );

        return !!matchingWardName;
      }),
    };
    return [filteredTokyo];
  }
  return [];
};

/**
 * Filters Osaka area hotels to only include those matching 24 wards.
 */
const filterAreasByOsaka24Wards = (areasData: JapanArea[], osakaHotels: OsakaWardHotelSimple[]): JapanArea[] => {
  const osakaArea = areasData.find((area) => area.name === "Osaka");

  if (osakaArea) {
    const osaka24WardsNames = osakaHotels.map(
      (hotel) => hotel.name,
    );
    const filteredOsaka = {
      ...osakaArea,
      hotels: osakaArea.hotels?.filter((hotel) => {
        const hotelData = hotel as unknown as MixedHotelData;
        const englishName = hotelData?.item_name;
        const hotelName = hotelData?.properties?.hotel_name;
        const name = hotelData?.name;

        const matchingWardName = osaka24WardsNames.find(
          (wardName) =>
            wardName === englishName ||
            wardName === hotelName ||
            wardName === name ||
            (englishName && wardName.includes(englishName)) ||
            englishName?.includes(wardName) ||
            (hotelName && wardName.includes(hotelName)) ||
            hotelName?.includes(wardName),
        );
        return !!matchingWardName;
      }),
    };
    return [filteredOsaka];
  }
  return [];
};

/**
 * Applies brand and benefit filters to hotel areas, using a map of hotel benefits for efficient lookup.
 */
const applyBrandBenefitFilters = (
  areas: JapanArea[],
  mapFilterCriteria: { brands?: string[]; benefits?: string[] } | null,
  japanAreas: JapanAreaType[],
): JapanArea[] => {
  if (!mapFilterCriteria) return areas;

  const { brands, benefits } = mapFilterCriteria;
  const hasBrandOrBenefitFilter =
    (brands && brands.length > 0) || (benefits && benefits.length > 0);

  if (!hasBrandOrBenefitFilter) return areas;

  const hotelBenefitsMap = new Map<string, string[]>();
  japanAreas.forEach((area) => {
    area.hotels?.forEach((hotel) => {
      if (hotel?.documentId) {
        const hotelBenefits =
          hotel.amenities_and_services?.hotel_benefits?.map(
            (b: HotelBenefit) => b.documentId,
          ) || [];
        hotelBenefitsMap.set(hotel.documentId, hotelBenefits);
      }
    });
  });

  return areas.map((area) => {
    const filteredHotels =
      area.hotels?.filter((hotel) => {
        let matches = true;

        if (brands && brands.length > 0) {
          const hotelBrandId = hotel.properties?.brand?.[0]?.documentId;
          const brandMatch = hotelBrandId && brands.includes(hotelBrandId);
          matches = matches && brandMatch;
        }

        if (benefits && benefits.length > 0) {
          const hotelBenefits =
            hotelBenefitsMap.get(hotel.documentId || "") || [];
          const benefitMatch = benefits.some((benefitId) =>
            hotelBenefits.includes(benefitId),
          );
          matches = matches && benefitMatch;
        }

        return matches;
      }) || [];

    return {
      ...area,
      hotels: filteredHotels,
    };
  });
};

/**
 * Returns the section title for hotel slider based on selected region and area type.
 */
const getSectionTitleByRegion = (
  selectedRegion: SelectedLocation | null,
  japanAreas: JapanAreaType[],
): string => {
  if (!selectedRegion || selectedRegion.name === "Japan") {
    return "おすすめホテル";
  }

  if (selectedRegion.type === selectedLocationType.TOKYO_23_WARDS) {
    return "東京23区のおすすめホテル";
  }

  if (selectedRegion.type === selectedLocationType.OSAKA_24_WARDS) {
    return "大阪24区のおすすめホテル";
  }

  const matchingArea = japanAreas.find((area) => {
    return (
      area.item_name === selectedRegion.name ||
      area.area_name === selectedRegion.name ||
      area.title === selectedRegion.name
    );
  });

  let displayName = selectedRegion.name;
  if (matchingArea?.area_name) {
    displayName = matchingArea.area_name;
    return displayName + "のホテル";
  } else if (selectedRegion.type === selectedLocationType.REGION) {
    const regionNameMapping: Record<string, string> = {
      Tohoku: "東北",
      Kanto: "関東",
      Chubu: "中部",
      Kansai: "関西",
      Chugoku: "中国",
      Shikoku: "四国",
      Kyushu: "九州",
    };
    displayName = regionNameMapping[selectedRegion.name] || selectedRegion.name;
  }

  return getSpecialAreaName(displayName) + "のホテル";
};

const MapSearchSection = function MapSearchSection(
  props: IMapSearchSectionProps,
) {
  const {
    areasData,
    locale,
    brands,
    currentBrand,
    benefitList,
    defaultValue,
    brandList,
    currentCity,
    listHotel,
    brandBannerList,
    regionData,
    hotel,
    enrichedRegionData
  } = props;
  const { japanAreas, allLocaleHotels, fetchAllLocaleHotels } =
    useJapanAreasStore();
  const [filteredSections, setFilteredSections] = useState<
    IHotelListSliderProps[]
  >([]);

  const [selectedRegion, setSelectedRegion] = useState<SelectedLocation | null>(
    null,
  );
  const [isNavigatingFromSelect, setIsNavigatingFromSelect] = useState(false);
  const [allowMapNavigation, setAllowMapNavigation] = useState(false);
  const [isNavigatingFromMap, setIsNavigatingFromMap] = useState(false);
  const [isResettingDropdown, setIsResettingDropdown] = useState(false);
  const [hotelsWithPrice, setHotelsWithPrice] = useState<
    { triplaCode: number; minPrice: number }[]
  >([]);
  const { data: mapTextDatasource } = useGetMapTextDatasourceQuery();
  const { map, filter_modal } =
    mapTextDatasource?.homeDatasource?.map_text_datasource || {};
  const DEFAULT_HOTEL_OR_CITY_OPTION: SelectOption = {
    value: "",
    label: "エリア名／ホテル名を入力してください",
  };
  const {
    title,
    filter_button_text,
    search_result_field_text,
    button_see_more_text,
  } = map || {};
  const {
    button_reset_text,
    button_search_text,
    filter_modal_title,
    subtitle_brand,
    subtitle_conditions,
  } = filter_modal || {};

  const [mapFilterCriteria, setMapFilterCriteria] = useState<{
    brands?: string[];
    benefits?: string[];
  } | null>(null);

  const filteredAreasData = useMemo(() => {
    if (!mapFilterCriteria) {
      return areasData;
    }

    const { brands, benefits } = mapFilterCriteria;
    const hasBrandOrBenefitFilter =
      (brands && brands.length > 0) || (benefits && benefits.length > 0);

    if (!hasBrandOrBenefitFilter) {
      return areasData;
    }

    const hotelBenefitsMap = new Map<string, string[]>();
    japanAreas.forEach((area) => {
      area.hotels?.forEach((hotel) => {
        if (hotel?.documentId) {
          const hotelBenefits =
            hotel.amenities_and_services?.hotel_benefits?.map(
              (b: HotelBenefit) => b.documentId,
            ) || [];
          hotelBenefitsMap.set(hotel.documentId, hotelBenefits);
        }
      });
    });

    const result = areasData.map((area) => {
      const filteredHotels =
        area.hotels?.filter((hotel) => {
          let matches = true;

          if (brands && brands.length > 0) {
            const hotelBrandId = hotel.properties?.brand?.[0]?.documentId;
            const brandMatch = hotelBrandId && brands.includes(hotelBrandId);
            matches = matches && brandMatch;
          }

          if (benefits && benefits.length > 0) {
            const hotelBenefits =
              hotelBenefitsMap.get(hotel.documentId || "") || [];
            const benefitMatch = benefits.some((benefitId) =>
              hotelBenefits.includes(benefitId),
            );
            matches = matches && benefitMatch;
          }

          return matches;
        }) || [];

      return {
        ...area,
        hotels: filteredHotels,
      };
    });

    return result;
  }, [areasData, japanAreas, mapFilterCriteria]);
  

  // Transform lean hotel data for map display
  // Uses hotel prop which only contains coords and names (reduces HTML payload by ~500KB+)
  const transformedHotelMetaData = useMemo(() => {
    if (!hotel?.japanHotels || hotel.japanHotels.length === 0) return [];

    // Filter out null/undefined hotels and transform them for map display
    const allHotels = hotel.japanHotels.filter(
      (hotelItem): hotelItem is NonNullable<typeof hotelItem> => hotelItem != null
    );

    return transformHotelsForMap(allHotels);
  }, [hotel]);

  useEffect(() => {
    const unsubscribe = hotelListEmitter.subscribe((sections) => {
      setFilteredSections(sections);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    const isDraftMode = globalThis.location.href.includes("status=DRAFT");

    const shouldFetch =
      !allLocaleHotels ||
      (japanAreas?.length > 0 && Object.keys(allLocaleHotels).length === 0);

    if (shouldFetch) {
      fetchAllLocaleHotels(isDraftMode);
    }
  }, [allLocaleHotels, japanAreas, fetchAllLocaleHotels]);

  const memoizedHotelSliderSections = useMemo(
    () => props.hotelSliderSections,
    [props.hotelSliderSections],
  );

  const fetchDistanceMatrixRef = useRef<(hotelId: string) => Promise<void>>();
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentHotelIdRef = useRef<string | null>(null);
  const hasHydrated = useHasHydrated();
  const { formData, setFormData, configurations: searchAvailableRoom } = useSearchFormStore();
  const searchRoomData = getSearchRoomData(searchAvailableRoom, {} as Record<string, string>);
  const default_adult_count = searchRoomData?.rooms_configurations?.default_adult_count;

  fetchDistanceMatrixRef.current = async (hotelId: string) => {
    if (currentHotelIdRef.current === hotelId) {
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    currentHotelIdRef.current = hotelId;

    const url = `${getPublicEnv('NEXT_PUBLIC_STRAPI_API_URL')}/mystays/distance-matrix-hotel/${hotelId}`;
    try {
      const response = await api.get(url, {
        signal: abortController.signal,
      });
      const hotelIds = Array.isArray(response?.data)
        ? response.data.map((destination) => ({
          documentId: destination?.destination?.documentId,
          distanceKm: destination?.distanceKm || 0,
        }))
        : [];

      let hotelList = japanAreas
        .flatMap((area) => area.hotels)
        .filter((hotel) =>
          [{ documentId: hotelId, distanceKm: 0 }, ...hotelIds].some(
            (item) => item.documentId === hotel?.documentId,
          ),
        )
        .map((hotel) => {
          const distanceData = hotelIds.find(
            (item) => item.documentId === hotel?.documentId,
          );
          return {
            ...hotel,
            distanceKm: distanceData?.distanceKm || 0,
          };
        })
        .sort((a, b) => (a.distanceKm || 0) - (b.distanceKm || 0));

      if (mapFilterCriteria) {
        const { brands: brandFilter, benefits: benefitFilter } =
          mapFilterCriteria;

        if (brandFilter && brandFilter.length > 0) {
          hotelList = hotelList.filter((hotel) => {
            const hotelBrandId = hotel?.properties?.brand?.[0]?.documentId;
            return hotelBrandId && brandFilter.includes(hotelBrandId);
          });
        }

        if (benefitFilter && benefitFilter.length > 0) {
          hotelList = hotelList.filter((hotel) => {
            const hotelBenefits =
              hotel?.amenities_and_services?.hotel_benefits?.map(
                (b: HotelBenefit) => b.documentId,
              ) || [];
            return benefitFilter.some((benefitId) =>
              hotelBenefits.includes(benefitId),
            );
          });
        }
      }

      const nearbySection: IHotelListSliderProps = {
        sectionTitle: getSectionTitle(),
        data: hotelList.map((hotel) => {
          const fullHotelData = memoizedHotelSliderSections
            .flatMap((section) => section.data)
            .find((h) => h.documentId === hotel?.documentId);

          if (fullHotelData) {
            return {
              ...fullHotelData,
              distanceKm: hotel.distanceKm,
            };
          }
          const thumbnail = getAreaHotelThumbnail(hotel as JapanHotel);
          let city = selectedRegion?.name;

          if (
            selectedRegion?.name === "Tokyo 23 Special Wards" ||
            selectedRegion?.name === "Tokyo"
          ) {
            city = "東京";
          } else if (
            selectedRegion?.name === "Osaka 24 Special Wards" ||
            selectedRegion?.name === "Osaka"
          ) {
            city = "大阪";
          }

          return {
            documentId: hotel?.documentId,
            name: hotel?.display_name || hotel?.item_name,
            reviewCount: hotel?.properties?.reviews || "0",
            rating: hotel?.properties?.review_score || "0",
            city,
            description: hotel?.properties?.hotel_overview_description || "",
            image: thumbnail || {},
            brand: hotel?.properties?.brand?.[0]?.documentId || "",
            service:
              hotel?.amenities_and_services?.amenities_and_services_customs ||
              [],
            btnText: "詳細を見る",
            benefits:
              hotel?.amenities_and_services?.hotel_benefits?.map(
                (b: HotelBenefit) => b.documentId,
              ) || [],
            lat: hotel?.properties?.latitude,
            lng: hotel?.properties?.longitude,
            slug: hotel?.slug,
            withHotSprings:
              hotel?.amenities_and_services?.amenities_and_services_customs?.some(
                (service: ServiceWithDocumentId) =>
                  service.documentId === "HOT_SPRING_AMENITY",
              ) || false,
            externalUrl: hotel?.redirect?.external_url,
            triplaCode: hotel?.external_ids?.tripla_backend_code,
            brandItemName: hotel?.properties?.brand?.[0]?.item_name,
            weather: hotel?.weather || undefined,
            brandDocId: hotel?.properties?.brand?.[0]?.documentId,
            distanceKm: hotel.distanceKm,
          };
        }),
        typeGroup: TYPE_GROUP.BASIC,
        amenitiesAndServiceData:
          props.hotelSliderSections[0]?.amenitiesAndServiceData,
        revealEdge: true,
        itemPerSlide: 4,
      };

      hotelListEmitter.emit([nearbySection]);
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }
    } finally {
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
        currentHotelIdRef.current = null;
      }
    }
  };

  /**
   * Sorts hotels by region, prefecture, and hotel order using regionData.
   * Builds order maps for efficient sorting.
   */
  const sortHotelsByOrder = useCallback(
    (hotels: THotelData[]): THotelData[] => {
      const hotelOrderMap = new Map<
        string,
        { order: number; regionOrder: number; prefectureOrder: number }
      >();

      if (!regionData?.datasourceJapans?.[0]?.region_datasource) {
        return hotels;
      }

      const regionDatasource = regionData.datasourceJapans[0].region_datasource;

      const regionOrderMap = new Map<string, number>();
      regionDatasource.selected_regions?.forEach(
        (selectedRegion: SelectedRegionItem) => {
          const typedRegion = selectedRegion;
          if (typedRegion?.documentId && typedRegion?.order) {
            regionOrderMap.set(typedRegion.documentId, typedRegion.order);
          }
        },
      );

      const prefectureOrderMap = new Map<
        string,
        { regionOrder: number; prefectureOrder: number }
      >();

      regionDatasource.regions?.forEach(
        (region: RegionDatasourceItem | null) => {
          if (!region?.documentId) return;

          const regionOrder = regionOrderMap.get(region.documentId) || 999;

          region.cities_order_on_top_page?.forEach((city: CityWithOrder) => {
            if (city?.documentId && city?.order) {
              prefectureOrderMap.set(city.documentId, {
                regionOrder,
                prefectureOrder: city.order,
              });
            }
          });
        },
      );

      japanAreas.forEach((area) => {
        const prefectureInfo = prefectureOrderMap.get(area.documentId || "");

        if (prefectureInfo && area.selected_hotels) {
          area.selected_hotels.forEach(
            (selectedHotel: { documentId: string; order: number }) => {
              hotelOrderMap.set(selectedHotel.documentId, {
                order: selectedHotel.order,
                regionOrder: prefectureInfo.regionOrder,
                prefectureOrder: prefectureInfo.prefectureOrder,
              });
            },
          );
        }
      });

      return hotels.sort((a, b) => {
        const orderA = hotelOrderMap.get(a.documentId || "");
        const orderB = hotelOrderMap.get(b.documentId || "");

        if (!orderA && !orderB) return 0;
        if (!orderA) return 1;
        if (!orderB) return -1;

        if (orderA.regionOrder !== orderB.regionOrder) {
          return orderA.regionOrder - orderB.regionOrder;
        }

        if (orderA.prefectureOrder !== orderB.prefectureOrder) {
          return orderA.prefectureOrder - orderB.prefectureOrder;
        }

        return orderA.order - orderB.order;
      });
    },
    [regionData, japanAreas],
  );

  // Transform Tokyo hotels from lean hotel data (memoized to prevent infinite re-renders)
  const tokyoHotelsAlt = useMemo(() => {
    const tokyoHotels =
      hotel?.japanHotels?.filter(
        (hotelItem) => hotelItem?.properties?.prefecture === "東京",
      ) || [];
    return transformJapanHotels(tokyoHotels as Parameters<typeof transformJapanHotels>[0]);
  }, [hotel]);

  // Transform Osaka hotels from lean hotel data (memoized to prevent infinite re-renders)
  const osakaHotelsAlt = useMemo(() => {
    const osakaHotels =
      hotel?.japanHotels?.filter(
        (hotelItem) => hotelItem?.properties?.prefecture === "大阪",
      ) || [];
    return transformOsakaHotels(osakaHotels as Parameters<typeof transformOsakaHotels>[0]);
  }, [hotel]);

  /**
   * Returns filtered areas for hotel list based on selected region and applied filters.
   */
  const getFilteredAreasForList = useCallback(() => {
    let baseFilteredAreas = areasData;

    if (selectedRegion?.name && selectedRegion?.name !== "Japan") {
      if (selectedRegion.type === selectedLocationType.REGION) {
        baseFilteredAreas = filterAreasByRegion(areasData, selectedRegion.name);
      } else if (selectedRegion.type === selectedLocationType.PREFECTURE) {
        baseFilteredAreas = areasData.filter((area) => {
          const areaName = area.name || "";
          return areaName === selectedRegion.name;
        });
      } else if (selectedRegion.type === selectedLocationType.TOKYO_23_WARDS) {
        baseFilteredAreas = filterAreasByTokyo23Wards(areasData, tokyoHotelsAlt);
      } else if (selectedRegion.type === selectedLocationType.OSAKA_24_WARDS) {
        baseFilteredAreas = filterAreasByOsaka24Wards(areasData, osakaHotelsAlt);
      }
    }

    baseFilteredAreas = applyBrandBenefitFilters(
      baseFilteredAreas,
      mapFilterCriteria,
      japanAreas,
    );

    return baseFilteredAreas;
  }, [areasData, japanAreas, mapFilterCriteria, selectedRegion, tokyoHotelsAlt, osakaHotelsAlt]);

  /**
   * Returns sorted and deduplicated hotels from filtered areas data.
   * Uses memoized hotel slider sections and fallback to area data if needed.
   */
  const getHotelsFromFilteredAreasData = useCallback((): THotelData[] => {
    const allHotels: THotelData[] = [];
    const filteredAreas = getFilteredAreasForList();

    filteredAreas.forEach((area) => {
      if (area.hotels && area.hotels.length > 0) {
        area.hotels.forEach((hotel) => {
          let fullHotelData = memoizedHotelSliderSections
            .flatMap((section) => section.data)
            .find((h) => h.documentId === hotel?.documentId);

          if (!fullHotelData && hotel?.documentId) {
            const fullHotelFromJapanAreas = japanAreas
              .flatMap((area) => area.hotels)
              .find((h) => h?.documentId === hotel.documentId);

            if (fullHotelFromJapanAreas) {
              fullHotelData = convertJapanAreasHotelToTHotelData(
                fullHotelFromJapanAreas,
                area,
                props.brandList,
              );
            }
          }

          if (fullHotelData) {
            allHotels.push(fullHotelData);
          } else {
            const hotelData = createHotelDataFromAreaData(
              hotel as JapanHotel,
              area,
              props.brandList,
            );
            allHotels.push(hotelData);
          }
        });
      }
    });

    const uniqueHotels = Array.from(
      allHotels
        .reduce(
          (m, h) => (h.documentId ? m.set(h.documentId, h) : m),
          new Map<string, THotelData>(),
        )
        .values(),
    );

    return sortHotelsByOrder(uniqueHotels);
  }, [
    getFilteredAreasForList,
    japanAreas,
    memoizedHotelSliderSections,
    props.brandList,
    sortHotelsByOrder,
  ]);

  const getSectionTitle = useCallback(() => {
    return getSectionTitleByRegion(selectedRegion, japanAreas);
  }, [selectedRegion, japanAreas]);

  useEffect(() => {
    const hotelsFromMap = getHotelsFromFilteredAreasData();
    const sectionTitle = getSectionTitle();

    const syncedSection: IHotelListSliderProps = {
      typeGroup: TYPE_GROUP.BASIC,
      sectionTitle: sectionTitle,
      data: hotelsFromMap,
      amenitiesAndServiceData:
        props.hotelSliderSections[0]?.amenitiesAndServiceData,
      revealEdge: true,
      itemPerSlide: 4,
    };

    setFilteredSections([syncedSection]);
  }, [
    getHotelsFromFilteredAreasData,
    getSectionTitle,
    props.hotelSliderSections,
  ]);

  const handleHotelClick = useCallback(async (hotelDocumentId: string) => {
    if (fetchDistanceMatrixRef.current) {
      await fetchDistanceMatrixRef.current(hotelDocumentId);
    }
  }, []);

  /**
   * Handles region selection from map or dropdown, updates form and dropdown state accordingly.
   */
  const handleRegionSelect = useCallback(
    (selectedLocation: SelectedLocation, fromMap: boolean = false) => {
      setSelectedRegion(selectedLocation);

      if (fromMap || isNavigatingFromMap) {
        setIsResettingDropdown(true);
        setValue("hotelOrCity", DEFAULT_HOTEL_OR_CITY_OPTION);
        setIsNavigatingFromMap(false);
        setTimeout(() => {
          setIsResettingDropdown(false);
        }, 50);
      }

      if (isNavigatingFromSelect) {
        setIsNavigatingFromSelect(false);
      }

      if (
        (selectedLocation.type === selectedLocationType.PREFECTURE ||
          (selectedLocation.type === selectedLocationType.REGION &&
            selectedLocation.name === "Hokkaido")) &&
        selectedLocation.name &&
        !isNavigatingFromSelect
      ) {
        let cityOption: SelectOption | undefined;
        const searchName = selectedLocation.name;
        let japaneseSearchName: string | undefined;

        const matchingArea = japanAreas.find((area) => {
          return (
            area.item_name === searchName ||
            area.area_name === searchName ||
            (area.title && area.title === searchName)
          );
        });

        if (matchingArea?.area_name) {
          japaneseSearchName = matchingArea.area_name;
        }

        for (const option of listHotelAndCityData) {
          if ("value" in option && typeof option.value === "string") {
            if (
              option.label === searchName ||
              option.label.toLowerCase() === searchName.toLowerCase() ||
              (japaneseSearchName && option.label === japaneseSearchName)
            ) {
              cityOption = option as SelectOption;
              break;
            }
          } else if ("options" in option && Array.isArray(option.options)) {
            const foundOption = option.options.find((subOption) => {
              const match =
                subOption.label === searchName ||
                subOption.label.toLowerCase() === searchName.toLowerCase() ||
                (japaneseSearchName && subOption.label === japaneseSearchName);
              return match;
            });
            if (foundOption) {
              cityOption = foundOption;
              break;
            }
          }
        }

        if (cityOption) {
          setValue("hotelOrCity", cityOption);
        }
      }
    },
    [
      isNavigatingFromMap,
      isNavigatingFromSelect,
      japanAreas,
      DEFAULT_HOTEL_OR_CITY_OPTION,
    ],
  );

  const isFirstCityChange = useRef(false);

  const [filterState, setFilterState] = useState<IMapFilterForm | null>(
    currentCity ? (defaultValue ?? null) : null,
  );

  const listHotelAndCityData = useMemo(() => {
    const result = groupJapanAreas(
      japanAreas,
      allLocaleHotels as Record<string, JapanHotel[]> | null | undefined,
      currentCity,
      "都市",
      "ホテル",
    );

    return result;
  }, [japanAreas, allLocaleHotels, currentCity]);
  const benefitsOption = useMemo(() => {
    return handleBenefitListData(benefitList);
  }, [benefitList]);
  const brandsOption = useMemo(() => {
    return handleBrandListData(brandList);
  }, [brandList]);
  const initialFormData = {
    hotelOrCity: findOption(
      listHotelAndCityData,
      filterState?.hotelOrCity?.value,
    ),
    checkIn: new Date(formData?.checkIn || DEFAULT_CHECKIN_DATE),
    checkout: new Date(formData?.checkout || DEFAULT_CHECKOUT_DATE),
    adults: formData?.rooms?.[0]?.adults || Number(default_adult_count) || Number(DEFAULT_ADULTS),
    promoCode: formData?.promoCode || "",
    rooms: formData?.rooms?.length
      ? formData?.rooms
      : [
        {
          adults: Number(default_adult_count) || Number(DEFAULT_ADULTS),
          children: [],
        },
      ],
    preferences: formData?.preferences ?? [],
    brands: formData?.brands ?? [],
  };
  const methods = useForm<IMapFilterForm>({
    defaultValues: initialFormData,
    resolver: valibotResolver(schemaSearchAvailableRoom),
  });

  const { handleSubmit, watch, getValues, setValue } = methods;

  const hotelOrCity = watch("hotelOrCity");

  /**
   * Finds hotel option by documentId from hotel/city data or japanAreas.
   */
  const findHotelOptionById = (
    hotelDocumentId: string,
  ): SelectOption | undefined => {
    if (listHotelAndCityData.length === 0 && japanAreas?.length > 0) {
      for (const area of japanAreas) {
        const hotel = area.hotels?.find(
          (h) => h?.documentId === hotelDocumentId,
        );
        if (hotel) {
          const tempOption: SelectOption = {
            value: hotelDocumentId,
            label:
              hotel.item_name ||
              hotel.hotel_name_in_mobile ||
              hotel.display_name ||
              "Unknown Hotel",
            type: TypeSearch.HOTEL,
            city: area.area_name || area.item_name,
            lat: hotel.properties?.latitude || undefined,
            lng: hotel.properties?.longitude || undefined,
          };
          return tempOption;
        }
      }
    }

    for (const option of listHotelAndCityData) {
      if (
        "value" in option &&
        typeof option.value === "string" &&
        "type" in option
      ) {
        if (
          option.value === hotelDocumentId &&
          option.type === TypeSearch.HOTEL
        ) {
          return option as SelectOption;
        }
      } else if ("options" in option && Array.isArray(option.options)) {
        const foundOption = option.options.find(
          (subOption) =>
            subOption.value === hotelDocumentId &&
            subOption.type === TypeSearch.HOTEL,
        );
        if (foundOption) {
          return foundOption;
        }
      }
    }
    return undefined;
  };

  /**
   * Handles custom events for map location select, hotel click, and dropdown location select.
   * Syncs region and hotel selection between map and dropdown UI.
   */
  useEffect(() => {
    const handleMapLocationSelect = (event: CustomEvent) => {
      const location = event.detail as SelectedLocation;

      setIsNavigatingFromMap(true);
      handleRegionSelect(location, true);

      if (
        (location.type === selectedLocationType.PREFECTURE ||
          (location.type === selectedLocationType.REGION &&
            location.name === "Hokkaido")) &&
        location.name
      ) {
        let cityOption: SelectOption | undefined;

        const targetArea = japanAreas.find(
          (area) =>
            area.item_name === location.name ||
            area.area_name === location.name ||
            area.title === location.name,
        );

        if (targetArea) {
          for (const option of listHotelAndCityData) {
            if ("value" in option && typeof option.value === "string") {
              if (option.value === targetArea.documentId) {
                cityOption = option as SelectOption;
                break;
              }
            } else if ("options" in option && Array.isArray(option.options)) {
              const foundOption = option.options.find(
                (subOption) => subOption.value === targetArea.documentId,
              );
              if (foundOption) {
                cityOption = foundOption;
                break;
              }
            }
          }
        }

        if (cityOption) {
          setIsResettingDropdown(true);
          setValue("hotelOrCity", cityOption);
          setTimeout(() => {
            setIsResettingDropdown(false);
          }, 100);
        }
      }
    };

    const handleMapHotelClick = (event: CustomEvent) => {
      const hotelDocumentId = event.detail as string;

      const hotelOption = findHotelOptionById(hotelDocumentId);
      if (hotelOption) {
        setIsNavigatingFromMap(true);
        setIsResettingDropdown(true);

        setTimeout(() => {
          setValue("hotelOrCity", hotelOption);

          setTimeout(() => {
            setIsNavigatingFromMap(false);
            setIsResettingDropdown(false);
          }, 50);
        }, 10);
      }

      fetchDistanceMatrixRef.current?.(hotelDocumentId);
    };

    const handleSelectLocationSelect = (event: CustomEvent) => {
      if (event.detail?.isResetEvent) {
        setSelectedRegion({
          name: event.detail.name,
          type: event.detail.type,
          documentId: event.detail.documentId,
        });
        return;
      }

      const directName = event.detail?.name;
      const directType = event.detail?.type;
      const directDocumentId = event.detail?.documentId;

      if (directName && directType) {
        const locationObj = {
          name: directName,
          type: directType,
          documentId: directDocumentId,
        };

        setSelectedRegion(locationObj);

        if (isNavigatingFromSelect) {
          setIsNavigatingFromSelect(false);
        }
      }
    };

    globalThis.addEventListener(
      "mapLocationSelect",
      handleMapLocationSelect as EventListener,
    );
    globalThis.addEventListener(
      "mapHotelClick",
      handleMapHotelClick as EventListener,
    );
    globalThis.addEventListener(
      "selectLocationSelect",
      handleSelectLocationSelect as EventListener,
    );

    return () => {
      globalThis.removeEventListener(
        "mapLocationSelect",
        handleMapLocationSelect as EventListener,
      );
      globalThis.removeEventListener(
        "mapHotelClick",
        handleMapHotelClick as EventListener,
      );
      globalThis.removeEventListener(
        "selectLocationSelect",
        handleSelectLocationSelect as EventListener,
      );
    };
  }, [
    findHotelOptionById,
    handleRegionSelect,
    isNavigatingFromSelect,
    japanAreas,
    listHotelAndCityData,
    setValue,
  ]);

  useEffect(() => {
    if (
      hotelOrCity?.value &&
      hotelOrCity?.label &&
      !isNavigatingFromSelect &&
      !isResettingDropdown &&
      !isNavigatingFromMap &&
      allowMapNavigation
    ) {
      if (hotelOrCity.type === TypeSearch.CITY) {
        const targetArea = japanAreas.find(
          (area) =>
            area.documentId === hotelOrCity.value ||
            area.area_name === hotelOrCity.label ||
            area.item_name === hotelOrCity.label ||
            area.title === hotelOrCity.label,
        );

        if (targetArea) {
          const locationName = targetArea.item_name || targetArea.area_name;

          let locationType = selectedLocationType.PREFECTURE;
          if (locationName === "Hokkaido") {
            locationType = selectedLocationType.REGION;
          }

          if (
            !selectedRegion ||
            selectedRegion?.name !== locationName ||
            selectedRegion?.type !== locationType
          ) {
            const eventDetail = {
              name: locationName,
              type: locationType,
              documentId: targetArea.documentId,
            };

            setIsNavigatingFromSelect(true);
            globalThis.dispatchEvent(
              new CustomEvent("selectLocationSelect", {
                detail: eventDetail,
              }),
            );

            setTimeout(() => {
              setIsNavigatingFromSelect(false);
            }, 100);
          }
        }
      } else if (hotelOrCity.type === TypeSearch.HOTEL) {
        const hotelDocumentId = hotelOrCity.value;
        let targetPrefecture: string | undefined;
        let targetArea: JapanAreaType | undefined;

        for (const area of japanAreas) {
          const hotel = area.hotels?.find(
            (h) => h?.documentId === hotelDocumentId,
          );
          if (hotel) {
            targetPrefecture = area.area_name || area.item_name;
            targetArea = area;
            break;
          }
        }

        if (targetPrefecture && targetArea) {
          const prefectureName = targetArea.item_name || targetArea.area_name;

          if (prefectureName === "Hokkaido") {
            return;
          }

          if (
            prefectureName === "Tokyo" &&
            selectedRegion?.type === selectedLocationType.TOKYO_23_WARDS
          ) {
            return;
          }

          if (
            prefectureName === "Osaka" &&
            selectedRegion?.type === selectedLocationType.OSAKA_24_WARDS
          ) {
            return;
          }

          if (
            !selectedRegion ||
            selectedRegion?.name !== prefectureName ||
            selectedRegion?.type !== selectedLocationType.PREFECTURE
          ) {
            setIsNavigatingFromSelect(true);
            globalThis.dispatchEvent(
              new CustomEvent("selectLocationSelect", {
                detail: {
                  name: prefectureName,
                  type: selectedLocationType.PREFECTURE,
                  documentId: targetArea.documentId,
                },
              }),
            );

            setTimeout(() => {
              setIsNavigatingFromSelect(false);
            }, 100);
          }
        }
      }
    }
  }, [
    japanAreas,
    selectedRegion,
    isNavigatingFromSelect,
    isResettingDropdown,
    isNavigatingFromMap,
    allowMapNavigation,
    hotelOrCity,
  ]);

  useEffect(() => {
    if (
      selectedRegion &&
      listHotelAndCityData.length > 0 &&
      (!hotelOrCity ||
        hotelOrCity.label === DEFAULT_HOTEL_OR_CITY_OPTION.label) &&
      (selectedRegion.type === selectedLocationType.PREFECTURE ||
        (selectedRegion.type === selectedLocationType.REGION &&
          selectedRegion.name === "Hokkaido")) &&
      !isNavigatingFromSelect &&
      !isResettingDropdown
    ) {
      const searchName = selectedRegion.name;
      let japaneseSearchName: string | undefined;

      const matchingArea = japanAreas.find((area) => {
        return (
          area.item_name === searchName ||
          area.area_name === searchName ||
          (area.title && area.title === searchName)
        );
      });

      if (matchingArea?.area_name) {
        japaneseSearchName = matchingArea.area_name;
      }
      let cityOption: SelectOption | undefined;

      for (const option of listHotelAndCityData) {
        if ("value" in option && typeof option.value === "string") {
          if (
            option.label === searchName ||
            option.label.toLowerCase() === searchName.toLowerCase() ||
            (japaneseSearchName && option.label === japaneseSearchName)
          ) {
            cityOption = option as SelectOption;
            break;
          }
        } else if ("options" in option && Array.isArray(option.options)) {
          const foundOption = option.options.find((subOption) => {
            const match =
              subOption.label === searchName ||
              subOption.label.toLowerCase() === searchName.toLowerCase() ||
              (japaneseSearchName && subOption.label === japaneseSearchName);

            return match;
          });
          if (foundOption) {
            cityOption = foundOption;
            break;
          }
        }
      }

      if (cityOption) {
        setValue("hotelOrCity", cityOption);
      }
    }
  }, [
    selectedRegion,
    listHotelAndCityData,
    hotelOrCity,
    isNavigatingFromSelect,
    isResettingDropdown,
    setValue,
    DEFAULT_HOTEL_OR_CITY_OPTION.label,
    japanAreas,
  ]);

  const getFilteredBrandOptions = () => {
    if (
      !hotelOrCity?.value ||
      hotelOrCity?.type !== TypeSearch.CITY ||
      !brandsOption ||
      !japanAreas
    ) {
      return brandsOption ?? [];
    }

    const selectedArea = japanAreas.find(
      (area) =>
        area.documentId === hotelOrCity.value ||
        area.area_name === hotelOrCity.label ||
        area.item_name === hotelOrCity.label ||
        area.title === hotelOrCity.label,
    );

    if (!selectedArea?.hotels) {
      return brandsOption ?? [];
    }

    const availableBrands = new Set<string>();

    selectedArea.hotels.forEach((hotel) => {
      if (!hotel) return;

      const hotelBrandDocumentId = hotel.properties?.brand?.[0]?.documentId;

      if (hotelBrandDocumentId) {
        const matchingBrand = brandsOption?.find(
          (brand) => brand.value === hotelBrandDocumentId,
        );
        if (matchingBrand) {
          availableBrands.add(matchingBrand.value);
        }
      }
    });

    return (
      brandsOption?.filter((brand) => availableBrands.has(brand.value)) ?? []
    );
  };
  const filteredBrandOptions = getFilteredBrandOptions();
  /**
   * Handles form submission, applies filters, updates state, and triggers booking or hotel page navigation.
   */
  const onSubmit: SubmitHandler<IMapFilterForm> = async (values: IMapFilterForm) => {
    setIsActionTriggered((pre) => !pre);
    scrollToElementWithOffset("map-title");

    setAllowMapNavigation(true);
    setTimeout(() => {
      setAllowMapNavigation(false);
    }, 500);

    const hasBrandOrBenefitFilter =
      (values.brands && values.brands.length > 0) ||
      (values.preferences && values.preferences.length > 0);

    const newMapFilterCriteria = hasBrandOrBenefitFilter
      ? {
        brands:
          values.brands
            ?.filter((item) =>
              filteredBrandOptions.some(
                (option) => option.value === item.value,
              ),
            )
            .map((b) => b.value) || [],
        benefits:
          values.preferences
            ?.filter((item) =>
              benefitsOption?.some((option) => option.value === item.value),
            )
            .map((p) => p.value) || [],
      }
      : null;
    if (values.hotelOrCity?.type === TypeSearch.CITY) {
      setValue(
        "brands",
        values.brands?.length
          ? values.brands.filter((item) =>
            filteredBrandOptions.some(
              (option) => option.value === item.value,
            ),
          )
          : [],
      );
      setValue(
        "preferences",
        values.preferences?.length
          ? values.preferences.filter((item) =>
            benefitsOption?.some((option) => option.value === item.value),
          )
          : [],
      );
    }
    setMapFilterCriteria(newMapFilterCriteria);
    // Store checkIn/checkout as YYYY-MM-DD strings ONLY in setFormData
    setFormData({
      ...values,
      checkIn: values.checkIn ? formatDateForStorage(values.checkIn, DEFAULT_CHECKIN_DATE) : DEFAULT_CHECKIN_DATE,
      checkout: values.checkout ? formatDateForStorage(values.checkout, DEFAULT_CHECKOUT_DATE) : DEFAULT_CHECKOUT_DATE,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }as any);
    // Keep setFilterState as Date for UI logic
    setFilterState(values);
    if (values.hotelOrCity?.type === TypeSearch.HOTEL) {
      const useTripla = values.hotelOrCity?.useTripla === true;

      if (useTripla) {
        const response = await fetch('/api/tripla/access-token');
        const tokenData = await response?.json() ?? {};
        const oneTimeToken = tokenData?.one_time_access_token ?? '';
        const queryParams = createBookingParams(values, {
          type: ERoomType.ROOMS,
          is_day_use: false,
          order: ERoomOrderBy.RECOMMENDED,
          is_including_occupied: false,
          locale: LOCALE_BASE_MAP[locale],
          coupon_code: values?.promoCode || "",
          ...(oneTimeToken && { one_time_access_token: oneTimeToken }),
        });

        if (TRIPLA_BOOKING_URL?.length && queryParams?.code?.length) {
          redirectBookingCard(
            {
              ...queryParams,
              checkin: queryParams?.checkin || DEFAULT_CHECKIN_DATE,
              checkout: queryParams?.checkout || DEFAULT_CHECKOUT_DATE,
              children: queryParams?.children,
              kids_ages: queryParams?.kids_ages,
            },
            true,
          );
        }
      } else {
        const targetUrl =
          values.hotelOrCity?.slug || values.hotelOrCity?.externalUrl;

        if (targetUrl) {
          globalThis.open(targetUrl);
        }
      }
    }

    setOpen(false);
  };

  const closeModal = () => {
    const currentValues = getValues();

    setRange({
      from: formData?.checkIn
        ? new Date(formData.checkIn)
        : new Date(DEFAULT_CHECKIN_DATE),
      to: formData?.checkout
        ? new Date(formData.checkout)
        : new Date(DEFAULT_CHECKOUT_DATE),
    });

    let hotelOrCityValue: SelectOption;

    if (!selectedRegion || selectedRegion?.name === "Japan") {
      hotelOrCityValue = DEFAULT_HOTEL_OR_CITY_OPTION;
    } else {
      let mapRegionOption: SelectOption | undefined;

      const matchingArea = japanAreas?.find((area) => {
        return (
          area?.item_name === selectedRegion?.name ||
          area?.area_name === selectedRegion?.name ||
          area?.title === selectedRegion?.name
        );
      });

      if (matchingArea) {
        for (const option of listHotelAndCityData) {
          if ("value" in option && typeof option.value === "string") {
            if (option?.value === matchingArea?.documentId) {
              mapRegionOption = option as SelectOption;
              break;
            }
          } else if ("options" in option && Array.isArray(option?.options)) {
            const foundOption = option?.options?.find(
              (subOption) => subOption?.value === matchingArea?.documentId,
            );
            if (foundOption) {
              mapRegionOption = foundOption;
              break;
            }
          }
        }
      }

      if (
        mapRegionOption &&
        currentValues?.hotelOrCity?.value !== mapRegionOption?.value
      ) {
        hotelOrCityValue = mapRegionOption;
      } else {
        hotelOrCityValue =
          currentValues.hotelOrCity || DEFAULT_HOTEL_OR_CITY_OPTION;
      }
    }

    methods.reset({
      hotelOrCity: hotelOrCityValue,
      checkIn: formData?.checkIn
        ? new Date(formData.checkIn)
        : new Date(DEFAULT_CHECKIN_DATE),
      checkout: formData?.checkout
        ? new Date(formData.checkout)
        : new Date(DEFAULT_CHECKOUT_DATE),
      promoCode: formData?.promoCode || "",
      rooms: formData?.rooms?.length
        ? formData.rooms
        : [
          {
            adults: Number(default_adult_count) || Number(DEFAULT_ADULTS),
            children: [],
          },
        ],
      preferences: formData?.preferences || [],
      brands: formData?.brands || [],
    });

    setOpen(false);
  };

  /**
   * Resets the filter form and map state to default values.
   */
  const handleReset = () => {
    setMapFilterCriteria(null);
    setSelectedRegion(null);

    globalThis.dispatchEvent(
      new CustomEvent("selectLocationSelect", {
        detail: {
          name: "Japan",
          type: selectedLocationType.REGION,
          isResetEvent: true,
        },
      }),
    );

    setRange({
      from: new Date(DEFAULT_CHECKIN_DATE),
      to: new Date(DEFAULT_CHECKOUT_DATE),
    });

    const defaultFormValues = {
      hotelOrCity: DEFAULT_HOTEL_OR_CITY_OPTION,
      checkIn: new Date(DEFAULT_CHECKIN_DATE),
      checkout: new Date(DEFAULT_CHECKOUT_DATE),
      promoCode: "",
      rooms: [
        {
          adults: Number(default_adult_count) || Number(DEFAULT_ADULTS),
          children: [],
        },
      ],
      preferences: [],
      brands: [],
    };

    methods.reset(defaultFormValues);
    setFormData(defaultFormValues);
    setFilterState(defaultFormValues);
    setIsActionTriggered((pre) => !pre);
  };
  const [open, setOpen] = useState(false);
  const [_isActionTriggered, setIsActionTriggered] = useState(false);
  const params = useMemo<HotelListParams>(() => {
    return {
      brandId: 297,
      rooms: [
        {
          checkin_date: formData?.checkIn
            ? formatDateToYYYYMMDD(formData.checkIn)
            : DEFAULT_CHECKIN_DATE,
          checkout_date: formData?.checkout
            ? formatDateToYYYYMMDD(formData.checkout)
            : DEFAULT_CHECKOUT_DATE,
          adults: formData?.rooms?.[0]
            ? formData?.rooms?.[0]?.adults.toString()
            : DEFAULT_ADULTS,
          children: formData?.rooms?.[0]
            ? formData?.rooms?.[0]?.children.length.toString()
            : "0",
          kids_age_range: [],
        },
      ],
      hotel_codes: listHotel ?? [],
    };
  }, [listHotel, formData?.checkIn, formData?.checkout, formData?.rooms]);
  /**
   * Fetches Tripla price data for hotels in batches and updates state with prices.
   */
  const getTripla = useCallback(async () => {
    if (!params || !hasHydrated || listHotel?.length == 0) {
      return;
    }
    try {
      const { hotel_codes, ...rest } = params;
      const batches = chunkArray(hotel_codes, 5);
      const batchPromises = batches.map((batch) =>
        getLowestPriceHotels({
          ...rest,
          hotel_codes: batch,
        }),
      );
      const results = await Promise.allSettled(batchPromises);
      const successfulResults = results
        .filter((result) => result.status === "fulfilled")
        .map((result) => result.value);
      const allHotelsWithPrice = successfulResults.flatMap(
        ({ data: apiHotels = [] }) =>
          apiHotels.map(({ lowest_price, id }) => ({
            triplaCode: id,
            minPrice: lowest_price || 0,
          })),
      );
      setHotelsWithPrice(allHotelsWithPrice);
    } catch (err) {
      console.error("Error fetching tripla prices:", err);
    }
  }, [params, hasHydrated, listHotel]);
  useEffect(() => {
    getTripla();
  }, [getTripla]);

  useEffect(() => {
    if (defaultValue && currentCity) {
      setFilterState(defaultValue);
    }
  }, [defaultValue, currentCity]);

  const { Adjust } = iconList;
  const [range, setRange] = useState<DateRange>({
    from: new Date(DEFAULT_CHECKIN_DATE),
    to: new Date(DEFAULT_CHECKOUT_DATE),
  });

  return (
    <div
      className="h-max w-full bg-[var(--fill-primary-tonal-lighter)] pt-10"
      id="map-title"
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col items-center justify-center gap-0">
            <div className="flex flex-col gap-7 lg:flex-row">
              <h2 className="text-center text-xl leading-9 font-[var(--font-semibold)] text-[var(--fill-base-main)] md:text-2xl">
                {title || " 地図から探す"}
              </h2>
              <Button
                className="mx-auto w-full py-3 text-center text-xs text-white md:text-sm lg:w-fit"
                onClick={() => {
                  methods.reset({
                    hotelOrCity: getValues("hotelOrCity"),
                    checkIn: formData?.checkIn
                      ? new Date(formData.checkIn)
                      : new Date(DEFAULT_CHECKIN_DATE),
                    checkout: formData?.checkout
                      ? new Date(formData.checkout)
                      : new Date(DEFAULT_CHECKOUT_DATE),
                    promoCode: formData?.promoCode || "",
                    rooms: formData?.rooms?.length
                      ? formData.rooms
                      : [
                        {
                          adults: Number(default_adult_count) || Number(DEFAULT_ADULTS),
                          children: [],
                        },
                      ],
                    preferences: formData?.preferences || [],
                    brands: formData?.brands || [],
                  });
                  setRange({
                    from: new Date(formData?.checkIn || DEFAULT_CHECKIN_DATE),
                    to: new Date(formData?.checkout || DEFAULT_CHECKOUT_DATE),
                  });
                  setOpen(true);
                }}
                type="button"
              >
                <Adjust />
                {filter_button_text || "条件を絞り込む"}
              </Button>
              <Modal
                containerClassName=" bg-white w-[calc(100%-32px)] lg:w-[700px] overflow-hidden  gap-0 rounded-xl max-md:!max-h-[90dvh]"
                closeClassName="bg-[var(--fill-base-main)] top-2 text-white rounded-full opacity-100 p-1 lg:p-1.5 "
                footer={<></>}
                header={
                  <>
                    <div className="flex w-full items-center border-t border-t-[var(--bg-reset-button)] p-3 pb-0 lg:px-6 lg:py-4 lg:pb-2">
                      <div className="mr-2 hidden h-6 w-1 bg-[var(--stroke-base-primary)] lg:block" />
                      <h2 className="text-md text-xl leading-8 font-[var(--font-semibold)] text-[var(--stroke-base-primary)]">
                      {filter_modal_title || "空室の絞り込み"}
                      </h2>
                    </div>{" "}
                    <Divider />
                  </>
                }
                open={open}
                toggleModal={closeModal}
              >
                <>
                  <MapFilterCheckboxes
                    filteredBrandOptions={filteredBrandOptions}
                    benefitOption={benefitsOption}
                    currentBrand={currentBrand}
                    listOption={listHotelAndCityData}
                    locale={locale}
                    subtitleBrand={subtitle_brand || "ブランド"}
                    subtitleConditions={subtitle_conditions || "条件の絞り込み"}
                    range={range}
                    setRange={setRange}
                  />
                  <hr className="text-[var(--stroke-gray-light)]" />
                  <div
                    className={`mx-auto flex w-full gap-4 bg-white px-4 pt-3 pb-4 md:w-full md:justify-center`}
                  >
                    <Button
                      onClick={handleReset}
                      variant="neutral"
                      size="xl"
                      type="button"
                      className="flex h-[43px] w-full items-center justify-center gap-3 bg-[var(--bg-reset-button)] px-6 text-[13px] text-[var(--bg-map-section-header)] lg:max-w-[200px]"
                    >
                      <span>{button_reset_text || "リセット"}</span>
                    </Button>
                    <Button
                      variant="secondary"
                      size="xl"
                      onClick={handleSubmit(onSubmit)}
                      type="submit"
                      className="flex h-[43px] w-full items-center justify-center gap-3 bg-[var(--color-yellow-850)] px-6 text-[13px] lg:max-w-[200px]"
                    >
                      <span>{button_search_text || "検索"}</span>
                    </Button>
                  </div>
                </>
              </Modal>{" "}
            </div>
            <IsolatedMapComponent
              locale={locale}
              areasData={filteredAreasData}
              brands={brands}
              selectedRegion={selectedRegion}
              japanAreas={japanAreas as unknown as JapanArea}
              onLocationSelect={handleRegionSelect}
              onHotelClick={handleHotelClick}
              mapFilterCriteria={mapFilterCriteria}
              hotelMetaData={transformedHotelMetaData}
              tokyoHotelsAlt={tokyoHotelsAlt}
              enrichedRegionData={enrichedRegionData}
            />
          </div>
        </form>
      </FormProvider>

      <div className="relative w-full items-start justify-items-center gap-16 overflow-x-hidden px-4 pt-5 pb-0 font-[family-name:var(--font-geist-sans)] lg:-top-10 lg:p-0 lg:px-7">
        {filteredSections?.map((section, index) => {
          return (
            <HotelListSlider
              scale={0.9}
              key={`hotel-slider-${index}-${section.data[0]?.documentId || index}`}
              data={section.data}
              amenitiesAndServiceData={section.amenitiesAndServiceData}
              sectionTitle={section.sectionTitle || "おすすめホテル"}
              revealEdge={section.revealEdge}
              itemPerSlide={section.itemPerSlide}
              sliderId={`hotel-slider-${index}`}
              filterState={filterState}
              currentBrand={currentBrand}
              typeGroup={section.typeGroup}
              hotelsWithPrice={hotelsWithPrice}
              isFirstCityChange={isFirstCityChange}
              brandBannerList={brandBannerList}
              hotelCount={section.data.length}
              seeMoreText={button_see_more_text || "もっと見る"}
              searchResultFieldText={
                search_result_field_text || "件が見つかりました"
              }
            />
          );
        })}
      </div>
    </div>
  );
};

export default MapSearchSection;
